import React, { useMemo } from 'react';
import { Text, TextProps, StyleSheet } from 'react-native';
import styled, { DefaultTheme } from 'styled-components/native';
import { extractUrls } from '@matap/shared';
import { TextStyle } from 'react-native/Libraries/StyleSheet/StyleSheetTypes';
import { openURL } from '../../utils';

export interface AppTextProps extends TextProps {
  children?: any;
  variant?: 'giant' | 'title' | 'medium' | 'primary' | 'label' | 'chat' | 'small';
  textColor?: keyof DefaultTheme['colors']['text'];
  text?: string;
  textAlign?: TextStyle['textAlign'];
  bold?: boolean;
  font?: keyof DefaultTheme['fonts'];
  hyperLink?: boolean;
}

function BaseTextView({ variant = 'primary', hyperLink, children, text, ...rest }: AppTextProps) {
  const value = useMemo(() => {
    const val = children || text || '';
    if (!hyperLink || typeof val !== 'string') return val;

    // Use the shared utility
    const parsedParts = extractUrls(val);

    return parsedParts.map((item, index) => {
      if (item.isUrl && item.normalizedUrl) {
        return (
          <Text key={`link-${index}`} style={styles.link} onPress={() => openURL(item.normalizedUrl!)} accessibilityRole="link" onLongPress={rest.onLongPress}>
            {item.original}
          </Text>
        );
      }

      // Wraps regular text to stop style bleeding
      return (
        <Text key={`text-${index}`} onPress={rest.onPress} onLongPress={rest.onLongPress}>
          {item.original}
        </Text>
      );
    });
  }, [children, text, hyperLink, rest.onPress, rest.onLongPress]);

  const SelectedVariant = VariantMap[variant] || Primary;

  return <SelectedVariant {...rest}>{value}</SelectedVariant>;
}

// Base Styled Component
const Component = styled(Text)<AppTextProps>`
  font-family: ${({ theme, bold, font }) => (font ? theme.fonts[font] : bold ? theme.fonts.bold : theme.fonts.primary)};
  color: ${({ theme, textColor }) => theme.colors.text[textColor as any] || theme.colors.text.black_light};
  text-align: ${({ textAlign }) => textAlign || 'right'};
  flex-shrink: 1;
`;

// Variant Styles
export const Giant = styled(Component)`
  font-size: ${(props) => props.theme.fontSizes.text.giant}px;
`;
export const Title = styled(Component)`
  font-size: ${(props) => props.theme.fontSizes.text.title}px;
`;
export const Label = styled(Component)`
  font-size: ${(props) => props.theme.fontSizes.text.label}px;
`;
export const Medium = styled(Component)`
  font-size: ${(props) => props.theme.fontSizes.text.medium}px;
`;
export const Primary = styled(Component)`
  font-size: ${(props) => props.theme.fontSizes.text.primary}px;
`;
export const Small = styled(Component)`
  font-size: ${(props) => props.theme.fontSizes.text.small}px;
`;
export const Chat = styled(Component)`
  font-size: ${(props) => props.theme.fontSizes.text.chat}px;
`;

const VariantMap = {
  giant: Giant,
  title: Title,
  medium: Medium,
  primary: Primary,
  label: Label,
  small: Small,
  chat: Chat,
};

const styles = StyleSheet.create({
  link: {
    color: 'blue',
    textDecorationLine: 'underline',
  },
});

export const AppTextView = React.memo(BaseTextView);
